import * as pc from 'playcanvas';
import { WALL_DEFS } from '../wallDefs';

/**
 * 2D slab ray-AABB test.  Ray starts at (rx, rz), direction (dx, dz), t ∈ [0, 1].
 * `expand` pads the box outward so the camera stops slightly before the surface.
 * Returns the entry t, or null if the ray misses or the box is behind the ray.
 */
function rayVsAABB2D(
  rx: number, rz: number,
  dx: number, dz: number,
  wx: number, wz: number, whw: number, whd: number,
  expand = 0.4,
): number | null {
  const ew = whw + expand;
  const eh = whd + expand;
  let tmin = -Infinity;
  let tmax =  Infinity;

  if (Math.abs(dx) < 1e-10) {
    if (rx < wx - ew || rx > wx + ew) return null;
  } else {
    const tx1 = (wx - ew - rx) / dx;
    const tx2 = (wx + ew - rx) / dx;
    tmin = Math.max(tmin, Math.min(tx1, tx2));
    tmax = Math.min(tmax, Math.max(tx1, tx2));
  }

  if (Math.abs(dz) < 1e-10) {
    if (rz < wz - eh || rz > wz + eh) return null;
  } else {
    const tz1 = (wz - eh - rz) / dz;
    const tz2 = (wz + eh - rz) / dz;
    tmin = Math.max(tmin, Math.min(tz1, tz2));
    tmax = Math.min(tmax, Math.max(tz1, tz2));
  }

  if (tmax < tmin || tmax < 0 || tmin > 1) return null;
  return Math.max(0, tmin);
}

// Perimeter boundary walls — must stay in sync with arenaSize / scales in buildArena().
// Interior walls are in WALL_DEFS; these are kept separate so gameplay collision
// (PlayerController / ArenaRoom) is unaffected (arena bounds already clamp movement).
const BOUNDARY_WALLS = [
  { x:   0, z: -98, hw: 100, hd: 0.75 },  // North
  { x:   0, z:  98, hw: 100, hd: 0.75 },  // South
  { x: -98, z:   0, hw: 0.75, hd: 100 },  // West
  { x:  98, z:   0, hw: 0.75, hd: 100 },  // East
];

const YAW_SPEED   = Math.PI * 0.9; // radians/sec (free-cam orbit)
const PITCH_SPEED = Math.PI * 0.5; // radians/sec
const MOUSE_YAW_SENSITIVITY   = 0.003; // radians per pixel of mouse movementX
const MOUSE_PITCH_SENSITIVITY = 0.002; // radians per pixel of mouse movementY
const MIN_PITCH   = 0.20;          // ~11° — near horizontal
const MAX_PITCH   = 1.30;          // ~74° — near top-down
const ORBIT_DIST  = 6;             // follow distance (smaller = closer)

export class CameraSystem {
  private cameraEntity: pc.Entity;
  private orbitYaw   = 0;
  private orbitPitch = 0.25; // ~30° elevation from ground
  private playerTarget = new pc.Vec3();

  constructor(app: pc.Application) {
    this.cameraEntity = new pc.Entity('camera');
    this.cameraEntity.addComponent('camera', {
      fov: 55,
      farClip: 500,
      nearClip: 0.1,
      clearColor: new pc.Color(0.04, 0.04, 0.10, 1),
    });
    app.root.addChild(this.cameraEntity);
  }

  follow(
    playerX: number,
    playerZ: number,
    orbitH: number, // -1/0/+1 horizontal orbit input
    orbitV: number, // -1/0/+1 tilt input
    speed: number,
    dt: number,
    mouseX = 0,     // raw mouse movementX (pixels, only when pointer-locked)
    mouseY = 0,     // raw mouse movementY (pixels, only when pointer-locked)
  ) {
    this.orbitYaw += orbitH * YAW_SPEED * dt;
    this.orbitPitch += orbitV * PITCH_SPEED * dt;

    // Mouse-look when pointer is locked (applied additively on top of keyboard)
    if (mouseX !== 0) this.orbitYaw += mouseX * MOUSE_YAW_SENSITIVITY;
    if (mouseY !== 0) this.orbitPitch -= mouseY * MOUSE_PITCH_SENSITIVITY;
    this.orbitPitch  = Math.max(MIN_PITCH, Math.min(MAX_PITCH, this.orbitPitch));

    // Camera position: spherical coords around the vehicle
    const hDist = ORBIT_DIST * Math.cos(this.orbitPitch);
    let camX  = playerX - Math.sin(this.orbitYaw) * hDist;
    const camY  = ORBIT_DIST * Math.sin(this.orbitPitch);
    let camZ  = playerZ  + Math.cos(this.orbitYaw) * hDist;

    // Spring-arm wall clip — pull camera in when the arm passes through a wall.
    // Test in XZ only; camY is kept constant so the camera doesn't drop to the floor.
    const armDX = camX - playerX;
    const armDZ = camZ - playerZ;
    let minT = 1.0;
    for (const w of [...WALL_DEFS, ...BOUNDARY_WALLS]) {
      const t = rayVsAABB2D(playerX, playerZ, armDX, armDZ, w.x, w.z, w.hw, w.hd);
      if (t !== null && t < minT) minT = t;
    }
    if (minT < 1.0) {
      camX = playerX + armDX * minT;
      camZ = playerZ + armDZ * minT;
    }

    // Set position directly — orbit should feel immediate, not springy
    this.cameraEntity.setPosition(camX, camY, camZ);

    // Always look exactly at the vehicle center
    this.playerTarget.set(playerX, 0, playerZ);
    this.cameraEntity.lookAt(this.playerTarget);

    // Subtle FOV zoom-out when moving fast
    const cam = this.cameraEntity.camera!;
    const targetFov = 55 + Math.abs(speed) * 1.5;
    cam.fov += (targetFov - cam.fov) * 0.05;
  }

  /** Orbit yaw in radians — used to transform WASD to world-space movement */
  getYaw(): number { return this.orbitYaw; }

  /** Force the orbit yaw to a specific angle — used to lock the camera behind the body */
  setYaw(angle: number): void { this.orbitYaw = angle; }

  get entity() { return this.cameraEntity; }

  /**
   * Project a world-space position to CSS pixel coords (origin = top-left of viewport).
   * Returns null when the point is behind the camera.
   */
  worldToScreen(worldPos: pc.Vec3): { x: number; y: number } | null {
    const screen = new pc.Vec3();
    this.cameraEntity.camera!.worldToScreen(worldPos, screen);
    if (screen.z <= 0) return null; // behind camera
    return { x: screen.x, y: screen.y };
  }

  screenShake(_intensity = 0.4, duration = 0.25) {
    document.body.classList.remove('shake');
    void document.body.offsetWidth;
    document.body.classList.add('shake');
    setTimeout(() => document.body.classList.remove('shake'), duration * 1000);
  }
}
